import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { get } from 'lodash';
import { CachingUtilityService } from '../../providers/caching/caching.utility.service';

@Injectable({
  providedIn: 'root'
})
export class LogoWidgetService {
  EMPTY = 'null';
  cachingUtilityService: CachingUtilityService;
  http: HttpClient;

  constructor(cachingUtilityService: CachingUtilityService, http: HttpClient) {
    this.EMPTY = 'null';
    this.cachingUtilityService = cachingUtilityService;
    this.http = http;
  }

  //eslint-disable-next-line
  getImage(serviceUrl: string, defaultUrl: any, successCallback: any): void {
    const cachedContent = this.cachingUtilityService.getTemplate(serviceUrl);
    if (cachedContent) {
      // one special rule:in case we queried third party for an image, and failed to get it,
      // we cache 'magic' value that tells us we don't need to try anymore
      if (cachedContent === this.EMPTY) {
        successCallback(serviceUrl, defaultUrl);
      } else {
        successCallback(serviceUrl, cachedContent);
      }
    } else {
      // otherwise, default to standard 'service' icon and immediately request logo for this service
      const domainOnly = this._getDomainFromUrl(serviceUrl);
      if (domainOnly === this.EMPTY) {
        this.cachingUtilityService.putTemplate(serviceUrl, this.EMPTY);
        successCallback(serviceUrl, defaultUrl);
      } else {
        this._requestData(serviceUrl, defaultUrl, domainOnly, successCallback);
      }
    }
  }

  //eslint-disable-next-line
  _getDomainFromUrl(serviceUrl: any): string {
    if (serviceUrl && serviceUrl.match) {
      //eslint-disable-next-line
      const match = serviceUrl.match(/^(?:https?:\/\/)?(?:[^@\n]+@)?(?:www\.)?([^:\/\n]+)/im);
      return get(match, '[1]', this.EMPTY);
    }
    return this.EMPTY;
  }

  /**
   * important:
   * logo.clearbit.com does not support HTTP verb OPTIONS, which is sent because of preflight/cors stuff.
   * It is up to the web browser to decide, when such request must be sent. In short, if request is treated as
   * 'simple', there will be no preflight request. Now, in order to qualify as 'simple' request, our call
   * to third party must fulfill certain conditions. See:
   * https://developer.mozilla.org/en-US/docs/Web/HTTP/CORS, chapter "Simple requests", that explains
   * these conditions.
   *
   * As we are using angular httpclient, a number of system-wide HTTP interceptors are applied - that might
   * alter our 'simple' request by adding 'complex' headers. Indeed, this is what happens with user.auth.interceptor,
   * that tries to attach 'authentication' token to our call to clearbit. The request is no longer 'simple',
   * and then we run into  CORS issue. To solve the problem, I whitelisted logo.clearbit.com in that interceptor.
   *
   * Just as a comment: theoretically, we could have prepared a plain vanilla XHRequest to circument all interceptors.
   * Like this:
   * const xhr = new XMLHttpRequest();
   * xhr.open(method, url, true);
   * xhr.responseType = "arraybuffer";
   * xhr.onload = function(result) { console.log('success response body:', xhr.response);}
   * xhr.onerror = function(result) { console.log('error');}
   * xhr.send();
   * However this is not recommended to do/as it bypasses angular framework.
   */
  //eslint-disable-next-line
  _requestData(serviceUrl: any, defaultUrl: any, domainOnly: any, successCallback: any): void {
    //eslint-disable-next-line
    const that = this;
    this.http
      .get('https://logo.clearbit.com/' + domainOnly, {
        observe: 'body',
        responseType: 'arraybuffer'
      })
      .toPromise()
      .then(
        response => {
          if (response) {
            let binary = '';
            const bytes = new Uint8Array(response);
            const len = bytes.byteLength;
            for (let i = 0; i < len; i++) {
              binary += String.fromCharCode(bytes[i]);
            }
            const base64coded = window.btoa(binary);

            const fullData = 'data:image/png;base64,' + base64coded;
            that.cachingUtilityService.putTemplate(serviceUrl, fullData);
            if (successCallback) {
              successCallback(serviceUrl, fullData);
            }
          }
        },
        //eslint-disable-next-line
        error => {
          that.cachingUtilityService.putTemplate(serviceUrl, that.EMPTY);
          successCallback(serviceUrl, defaultUrl);
        }
      );
  }
}
